import { ReactElement, useState } from 'react';
import { Divider, Group, MultiSelect, Stack, Switch, Text, UnstyledButton } from '@mantine/core';
import { APPOINTMENT_STATUS_OPTIONS } from '@bariendo/lib/constants';
import { AppointmentTypeSelect } from './AppointmentTypeSelect';
import { Practitioner, PractitionerRole } from '@medplum/fhirtypes';
import { PractitionerMultiSelect } from './PractitionerMultiSelect';
import { useSelectedPractitionerIds } from '../hooks/useSelectedPractitionerIds';
import { mapPractitionerIdsToProviderRefs } from '../utils/common';

interface ActivityDefinitionList {
  code: string;
  label: string;
  value: string;
  backgroundColor: string;
  borderColor: string;
  textColor: string;
}

interface SchedulingFilterProps {
  showAppointments?: boolean;
  onShowAppointmentsChange?: (value: boolean) => void;
  showAvailability?: boolean;
  onShowAvailabilityChange?: (value: boolean) => void;
  selectedRoles?: string[];
  onRolesChange?: (roles: string[]) => void;
  selectedProviders?: string[];
  onProvidersChange?: (providers: string[]) => void;
  selectedStatuses?: string[];
  onStatusesChange?: (statuses: string[]) => void;
  selectedLocations?: string[];
  onLocationsChange?: (locations: string[]) => void;
  locations: { value: string; label: string }[];
  activityDefinitionsList: ActivityDefinitionList[];
  roles: { value: string; label: string }[];
  selectedServiceType: string[];
  onServiceTypeChange: (serviceType: string[]) => void;
  practitioners: Practitioner[];
  practitionerRoles: PractitionerRole[];
}

const appointmentStatusData = APPOINTMENT_STATUS_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }));

export default function SchedulingFilter({
  showAppointments = true,
  onShowAppointmentsChange,
  showAvailability = true,
  onShowAvailabilityChange,
  selectedServiceType,
  onServiceTypeChange,
  selectedRoles: controlledRoles,
  onRolesChange,
  selectedProviders: controlledProviders,
  onProvidersChange,
  selectedStatuses: controlledStatuses,
  onStatusesChange,
  selectedLocations: controlledLocations,
  onLocationsChange,
  locations,
  activityDefinitionsList,
  roles,
  practitioners,
  practitionerRoles,
}: SchedulingFilterProps): ReactElement {
  const [internalRoles, setInternalRoles] = useState<string[]>([]);
  const [internalProviders, setInternalProviders] = useState<string[]>([]);
  const [internalStatuses, setInternalStatuses] = useState<string[]>([]);
  const [internalLocations, setInternalLocations] = useState<string[]>([]);

  const selectedRoles = onRolesChange !== undefined ? (controlledRoles ?? []) : internalRoles;
  const setSelectedRoles = onRolesChange ?? setInternalRoles;

  const selectedProviders = onProvidersChange !== undefined ? (controlledProviders ?? []) : internalProviders;
  const setSelectedProviders = onProvidersChange ?? setInternalProviders;

  const selectedStatuses = onStatusesChange !== undefined ? (controlledStatuses ?? []) : internalStatuses;
  const setSelectedStatuses = onStatusesChange ?? setInternalStatuses;

  const selectedLocations = onLocationsChange !== undefined ? (controlledLocations ?? []) : internalLocations;
  const setSelectedLocations = onLocationsChange ?? setInternalLocations;

  const hasActiveFilters =
    selectedServiceType.length > 0 ||
    selectedRoles.length > 0 ||
    selectedProviders.length > 0 ||
    selectedStatuses.length > 0 ||
    selectedLocations.length > 0;

  const handleClearFilters = () => {
    onServiceTypeChange([]);
    setSelectedRoles([]);
    setSelectedProviders([]);
    setSelectedStatuses([]);
    setSelectedLocations([]);
  };

  const selectedPractitionerIds = useSelectedPractitionerIds(selectedProviders, practitioners, practitionerRoles);

  const handleOnChangeIds = (ids: string[]) => {
    const newProviderRefs = mapPractitionerIdsToProviderRefs(ids, practitioners, practitionerRoles);
    setSelectedProviders(newProviderRefs);
  };

  return (
    <>
      <Group justify="space-between" align="center">
        <Text fw={600} size="md">
          Filters
        </Text>
        {hasActiveFilters && (
          <UnstyledButton
            p="4px 8px"
            style={{
              borderRadius: '4px',
              transition: 'background-color 0.2s ease-in-out',
              textDecoration: 'none',
            }}
            onClick={handleClearFilters}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <Text size="sm" c="blue">
              Remove all filters
            </Text>
          </UnstyledButton>
        )}
      </Group>

      <Stack gap="md">
        <AppointmentTypeSelect
          value={selectedServiceType}
          onChange={onServiceTypeChange}
          appointmentTypes={activityDefinitionsList}
        />

        <MultiSelect
          placeholder="All roles"
          data={roles}
          value={selectedRoles}
          onChange={setSelectedRoles}
          clearable
          searchable
        />

        <PractitionerMultiSelect
          valueIds={selectedPractitionerIds}
          onChangeIds={handleOnChangeIds}
          practitioners={practitioners}
        />

        <MultiSelect
          placeholder="All statuses"
          data={appointmentStatusData}
          value={selectedStatuses}
          onChange={setSelectedStatuses}
          clearable
          searchable
        />

        <MultiSelect
          placeholder="All locations"
          data={locations}
          value={selectedLocations}
          onChange={setSelectedLocations}
          clearable
          searchable
        />
        <Divider />
        {onShowAppointmentsChange && (
          <Switch
            label="Show Appointments"
            checked={showAppointments}
            onChange={(e) => onShowAppointmentsChange(e.currentTarget.checked)}
          />
        )}

        {onShowAvailabilityChange && (
          <Switch
            label="Show Availability"
            checked={showAvailability}
            onChange={(e) => onShowAvailabilityChange(e.currentTarget.checked)}
          />
        )}
      </Stack>
    </>
  );
}
